import React from "react";

import {
  MagnifyingGlassIcon,
  UserIcon,
  TicketIcon,
  DocumentTextIcon,
  UserGroupIcon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  KeyIcon,
  GiftIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

import { useSearch } from "../contexts/SearchContext";

const SearchResults = () => {
  const {
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleResultClick,
    searchQuery,
  } = useSearch();

  if (!showResults) return null;

  return (
    <>
      {/* Backdrop - hidden on mobile since parent modal handles it */}
      <div
        className="fixed inset-0 bg-black/25 z-40 hidden lg:block"
        onClick={() => setShowResults(false)}
      />

      {/* Results Panel */}
      <div className="lg:absolute lg:top-full left-0 right-0 lg:mt-2 bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl rounded-2xl shadow-2xl ring-1 ring-gray-200/60 dark:ring-white/[0.06] z-50 max-h-[60vh] lg:max-h-[70vh] overflow-auto">
        <div className="sticky top-0 bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl border-b border-gray-200/60 dark:border-white/[0.06] px-4 py-3 flex justify-between items-center">
          <h3 className="text-sm font-semibold tracking-tight text-gray-700 dark:text-gray-300">
            Results for{" "}
            <span className="text-gray-900 dark:text-white">
              "{searchQuery}"
            </span>
          </h3>
          <button
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            onClick={() => setShowResults(false)}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {isSearching ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center">
              <svg
                className="animate-spin h-5 w-5 mr-3 text-primary-600"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  fill="none"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  fill="currentColor"
                />
              </svg>
              <span className="text-gray-600 dark:text-gray-400">
                Searching...
              </span>
            </div>
          </div>
        ) : searchResults?.error ? (
          <div className="p-8 text-center text-red-600 dark:text-red-400">
            {searchResults.error}
          </div>
        ) : (
          <div className="p-2">
            {/* Registrations Section */}
            {searchResults?.registrations?.length > 0 && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Registrations
                </h4>
                {searchResults.registrations.map((registration) => (
                  <button
                    className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                    key={registration.registration_id}
                    onClick={() =>
                      handleResultClick(
                        "customer",
                        registration.registration_id
                      )
                    }
                  >
                    <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {registration.first_name} {registration.last_name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {registration.email_address}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Invoices Section (from transactions) */}
            {searchResults?.transactions?.length > 0 && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Invoices
                </h4>
                {searchResults.transactions.map((transaction) => (
                  <button
                    className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                    key={transaction.transaction_id}
                    onClick={() =>
                      handleResultClick("invoice", transaction.transaction_id)
                    }
                  >
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {transaction.metadata?.prettyInvoiceId ||
                          `Invoice-${transaction.transaction_id?.slice(0, 8)}`}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {transaction.metadata?.customerDetails?.name ||
                          transaction.metadata?.customerDetails?.email ||
                          "Customer"}{" "}
                        • ${transaction.amount} •{" "}
                        {transaction.subscription_type || "Premium"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Tickets Section */}
            {searchResults?.tickets?.length > 0 && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tickets
                </h4>
                {searchResults.tickets.map((ticket) => (
                  <button
                    className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                    key={ticket.ticket_id}
                    onClick={() =>
                      handleResultClick("ticket", ticket.ticket_number)
                    }
                  >
                    <TicketIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {ticket.subject}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {ticket.ticket_number} • {ticket.category} •
                        <span
                          className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-medium
                          ${
                            ticket.status === "open"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                              : ticket.status === "pending"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {ticket.status}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Ticket Comments Section */}
            {searchResults?.ticketComments?.length > 0 && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ticket Comments
                </h4>
                {searchResults.ticketComments.map((comment) => (
                  <button
                    className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                    key={comment.comment_id || comment.ticket_comment_id}
                    onClick={() =>
                      handleResultClick("ticket", comment.ticket?.ticket_number)
                    }
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                        {comment.content}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Ticket: {comment.ticket?.ticket_number} -{" "}
                        {comment.ticket?.subject}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Users Section */}
            {searchResults?.users?.length > 0 && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Users
                </h4>
                {searchResults.users.map((user) => (
                  <button
                    className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                    key={user.user_id}
                    onClick={() => handleResultClick("user", user.user_id)}
                  >
                    <UserGroupIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.username}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {user.domain}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Admins Section */}
            {searchResults?.admins?.length > 0 && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Admins
                </h4>
                {searchResults.admins.map((admin) => (
                  <button
                    className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                    key={admin.admin_id}
                    onClick={() => handleResultClick("admin", admin.admin_id)}
                  >
                    <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {admin.email_address}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Administrator
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* FAQ Section - supporting both formats */}
            {(searchResults?.faqs?.length > 0 ||
              searchResults?.faq?.length > 0) && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  FAQ
                </h4>
                {(searchResults.faqs || searchResults.faq || [])
                  .slice(0, 5)
                  .map((item) => (
                    <button
                      className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                      key={item.faq_id || item.id}
                      onClick={() => handleResultClick("faq")}
                    >
                      <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {item.question || item.q}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                          {item.category} • {item.answer || item.a}
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            )}

            {/* Lunova Keys Section */}
            {searchResults?.lunovaKeys?.length > 0 && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Lunova Keys
                </h4>
                {searchResults.lunovaKeys.map((key) => (
                  <button
                    className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                    key={key.id}
                    onClick={() => handleResultClick("lunovakey", key.id)}
                  >
                    <KeyIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {key.key || key.code}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {key.user_email || "Unassigned"} • {key.status} •{" "}
                        {key.created_at}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Redeem Codes Section */}
            {searchResults?.redeemCodes?.length > 0 && (
              <div className="mb-4">
                <h4 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  LunaKeys
                </h4>
                {searchResults.redeemCodes.map((code) => (
                  <button
                    className="w-full px-3 py-2 flex items-center hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                    key={code.redeem_code_id}
                    onClick={() =>
                      handleResultClick("lunakey", code.registration_id, {
                        redeemCodeId: code.redeem_code_id,
                      })
                    }
                  >
                    <GiftIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {code.code}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {code.status} • {code.active ? "Active" : "Inactive"}
                        {code.date_redeemed &&
                          ` • Redeemed: ${new Date(code.date_redeemed).toLocaleDateString()}`}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No Results */}
            {searchResults &&
              !searchResults.registrations?.length &&
              !searchResults.transactions?.length &&
              !searchResults.tickets?.length &&
              !searchResults.ticketComments?.length &&
              !searchResults.users?.length &&
              !searchResults.admins?.length &&
              !searchResults.faqs?.length &&
              !searchResults.faq?.length &&
              !searchResults.lunovaKeys?.length &&
              !searchResults.redeemCodes?.length && (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <MagnifyingGlassIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No results found for "{searchQuery}"</p>
                  <p className="text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                </div>
              )}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchResults;
